import * as d3 from 'd3';
import { binColor, type MonitorPalette } from '../../chart/palette';
import { localDayShort, tickFormatter } from '../../chart/format';
import type { G, MeetingMark, Model } from '../model';

const MAX_MEETING_LABELS = 7;

export function drawMeetings(
    g: G,
    model: Model,
    pal: MonitorPalette,
    highlight: number | null
): void {
    const { plotTop, axisY } = model.layout;
    const xs = model.visMeetings;
    const isHi = (m: MeetingMark) => m.idx === highlight;

    g.selectAll('line')
        .data(xs)
        .join('line')
        .attr('x1', m => model.x(m.date))
        .attr('x2', m => model.x(m.date))
        .attr('y1', plotTop)
        .attr('y2', axisY)
        .attr('stroke', m => (isHi(m) ? pal.ink : pal.inkFaint))
        .attr('stroke-width', m => (isHi(m) ? 1.4 : 0.8))
        .attr('stroke-dasharray', m => (isHi(m) ? '4 3' : '2 4'));

    g.selectAll('path')
        .data(xs)
        .join('path')
        .attr('d', m => {
            const px = model.x(m.date);
            return `M${px - 4} ${axisY}L${px + 4} ${axisY}L${px} ${axisY - 6}Z`;
        })
        .attr('fill', m => (isHi(m) ? pal.ink : pal.inkFaint));

    g.selectAll('text')
        .data(xs.length <= MAX_MEETING_LABELS ? xs : xs.filter(isHi))
        .join('text')
        .attr('x', m => model.x(m.date))
        .attr('y', axisY + 34)
        .attr('text-anchor', 'middle')
        .attr('font-size', 10)
        .attr('fill', m => (isHi(m) ? pal.ink : pal.inkFaint))
        .text(m => localDayShort(m.date));
}

export function drawStrip(g: G, model: Model): void {
    const { stripTop, stripH } = model.layout;

    g.selectAll('rect.hist')
        .data(model.vis)
        .join('rect')
        .attr('class', 'hist')
        .attr('x', d => model.x(d.date))
        .attr('y', stripTop)
        .attr('width', model.colW)
        .attr('height', stripH)
        .attr('fill', d => binColor(model.deltas[d.modal]))
        .attr('opacity', d => (d.flagged ? 0.28 : 0.85));
}

export function drawForwardStrip(g: G, model: Model): void {
    const { stripTop, stripH } = model.layout;

    g.selectAll('rect.fwd')
        .data(d3.range(model.nF))
        .join('rect')
        .attr('class', 'fwd')
        .attr('x', i => model.xF(i) - model.fwdColW / 2)
        .attr('y', stripTop)
        .attr('width', model.fwdColW)
        .attr('height', stripH)
        .attr('fill', i => binColor(model.deltas[model.fwdModal[i]]))
        .attr('opacity', 0.85);
}

export function drawAxis(g: G, model: Model, pal: MonitorPalette): void {
    const { marginLeft, split, axisY, isNarrow } = model.layout;

    g.append('line')
        .attr('x1', marginLeft)
        .attr('x2', split)
        .attr('y1', axisY)
        .attr('y2', axisY)
        .attr('stroke', pal.gridStrong);

    const format = tickFormatter(model.spanMs);

    g.selectAll('text')
        .data(model.x.ticks(isNarrow ? 3 : 5))
        .join('text')
        .attr('x', d => model.x(d))
        .attr('y', axisY + 18)
        .attr('text-anchor', 'middle')
        .attr('font-size', 11)
        .attr('fill', pal.inkMuted)
        .text(d => format(d));
}
